import { useAuthStore, withAuthStore } from 'context/AuthStore'
import { useResErrors, useStateCallback } from 'hooks'
import { AuthService, MiscService } from 'lib/api'
import { deepCopyObject } from 'lib/utils'

import moment from 'moment'
import { useSnackbar } from 'notistack'
import { createContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useLocation } from 'react-router-dom'

import { tableStatusBtns } from 'pages/Home/components/userPanel/const'

export const HomeStoreContext = createContext({
  homeStore: {},
  homeStoreFunc: {}
})

const initForms = {
  userUsername: undefined,
  description: undefined,
  imei: undefined,
  module: undefined,
  email: undefined,
  phone: undefined,
  subject: undefined,
  userPhotos: undefined,
  vin: undefined,
  dealId: undefined
}

export const paginationInitialState = {
  limit: 100,
  offset: 0,
  count: 0
}

const HomeStoreProvider = props => {
  const { id } = useParams()
  const location = useLocation()

  const {
    authStore: { user }
  } = useAuthStore()

  const _isMounted = useRef(true)
  const { standardCheck } = useResErrors()
  const { enqueueSnackbar } = useSnackbar()

  const [lists, setLists] = useState([])
  const [requestList, setRequestList] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useStateCallback(0)
  const [form, setForm] = useState(initForms)
  const [usersAdmin, setUsersAdmin] = useState([])
  const [wlTab, setWlTab] = useState(0)
  const [selectedStatus, setSelectedStatus] = useState(
    tableStatusBtns[wlTab].code
  )
  const [pagination, setPagination] = useState(paginationInitialState)
  const [message, setMessage] = useState('')
  const [chat, setChat] = useState([])
  const [openReviewModal, setOpenReviewModal] = useState(false)
  const [openRenewModal, setOpenRenewModal] = useState(false)

  const getInitialData = async () => fetchAdmin()

  const getSupportList = async (status, offset, limit) => {
    setLoading(true)

    const data = {
      createdBy: user.data?.username,
      limit,
      offset,
      status
    }

    await MiscService.getSupportList(data).then(res =>
      standardCheck(
        res,
        ({ data }) => {
          setLoading(false)
          setPagination(prevState => ({
            ...prevState,
            count: Math.ceil(data?.countFiltered / prevState.limit)
          }))
          setLists(
            data?.listFiltered?.map(item => {
              if (item.chat) {
                const chat = JSON.parse(item.chat)
                const lastMessage = chat[chat.length - 1]
                return {
                  ...item,
                  unAnswered: lastMessage.author !== user.data?.username
                }
              }

              return { ...item, unAnswered: false }
            })
          )
        },
        {
          onFail: () => {
            setLists([])
            setPagination({
              limit: 100,
              offset: 0,
              count: 0
            })
            setLoading(false)
          }
        }
      )
    )
  }

  const getSupportById = async id => {
    if (id) {
      setLoading(true)
      await MiscService.getSupportById(id).then(res =>
        standardCheck(
          res,
          ({ data }) => {
            setLoading(false)
            setRequestList(data)
            let newChat = data.chat ? JSON.parse(data.chat) : []
            if (data.comment && ['Завершен'].includes(data.status)) {
              newChat.push({
                complete: data.comment,
                author: data.adminUsername,
                date: data.completedDate,
                fullName: data.adminFullname,
                files: data.adminPhotos
              })
            }
            if (data.reason && ['Отказан', 'Отменен'].includes(data.status)) {
              newChat.push({
                reason: data.reason,
                fullName: data.adminFullname,
                author: data.adminUsername,
                date: data.completedDate
              })
            }
            setChat(newChat)
          },
          {
            onFail: () => {
              setRequestList([])
              setChat([])
              setLoading(false)
            }
          }
        )
      )
    }
  }

  const fetchAdmin = async () => {
    try {
      const userDataAdmin = await AuthService.getUsersAdmin()
      setUsersAdmin(userDataAdmin?.data)
    } catch (error) {
      console.error(error)
    }
  }

  const handleChangeForm = event => {
    const { name, value } = event.target

    setForm(prevState => ({
      ...prevState,
      [name]: value
    }))
  }

  const onPaginationChange = async (_, newValue) => {
    setPagination(prevState => ({ ...prevState, offset: newValue }))
    setLoading(true)

    await getSupportList(
      selectedStatus,
      newValue ? newValue - 1 : 0,
      pagination.limit
    )

    setLoading(false)
  }

  const onChangeTab = (_, newValue) => setSelectedTab(newValue, () => {})

  const MAX_FILE_SIZE = 10000000 // 10 MB

  const handleUpload = async (e, sendToChat = false) => {
    const target = e.target
    if (target?.files) {
      const file = target.files[0]
      if (file?.size >= MAX_FILE_SIZE) {
        enqueueSnackbar('Размер файла не может превышать 10мб')
        return
      }
      if (target?.files?.length !== 0) {
        const formData = new FormData()
        formData.append('file', target?.files[0])
        const res = await MiscService.uploadPhoto(formData)

        if (res?.data?.fileDownloadUri) {
          if (sendToChat) {
            handleSendChat({
              fileDownloadUri: res.data.fileDownloadUri,
              originalFileName: res.data.originalFileName,
              size: res.data.size
            })
          } else {
            setForm(prevState => ({
              ...prevState,
              userPhotos: [
                ...(prevState.userPhotos || []),
                {
                  fileDownloadUri: res.data.fileDownloadUri,
                  originalFileName: res.data.originalFileName,
                  size: res.data.size
                }
              ]
            }))
          }
        }
      }
    }
  }

  const handleDelete = src => {
    setForm(prevState => ({
      ...prevState,
      userPhotos: form?.userPhotos?.filter(f => f?.fileDownloadUri !== src)
    }))
  }

  const handleSendChat = async (file = null) => {
    if (!message.length && !file) {
      enqueueSnackbar('Заполните поле сообщения!', {
        variant: 'error'
      })
      return
    }

    await MiscService.updateSupportById({
      id,
      chat: JSON.stringify([
        ...chat,
        {
          message,
          fullName: `${user.data.firstName} ${user.data.lastName}`,
          author: user.data.username,
          date: moment().format(),
          file
        }
      ])
    }).then(res => {
      setChat(res.data.chat ? JSON.parse(res.data.chat) : chat)
      setMessage('')
    })
  }

  const onResetForm = () => setForm(deepCopyObject({ ...initForms }))

  useEffect(() => {
    getInitialData()

    return () => {
      _isMounted.current = false
    }
  }, [])

  useEffect(() => {
    getSupportById(id)
  }, [id])

  useEffect(() => {
    if (selectedTab === 1 && location.pathname === '/home') {
      getSupportList(selectedStatus, pagination.offset, pagination.limit)
    }
  }, [selectedTab, location.pathname])

  const homeStore = {
    id,
    lists,
    requestList,
    form,
    usersAdmin,
    message,
    chat,
    selectedTab,
    wlTab,
    pagination,
    loading,
    openReviewModal,
    openRenewModal
  }

  const homeStoreFunc = {
    getSupportList,
    setChat,
    setForm,
    onChangeTab,
    onPaginationChange,
    handleChangeForm,
    handleUpload,
    handleDelete,
    setWlTab,
    setSelectedStatus,
    handleSendChat,
    setMessage,
    onResetForm,
    setOpenReviewModal,
    setOpenRenewModal,
    setRequestList
  }

  return (
    <HomeStoreContext.Provider value={{ homeStore, homeStoreFunc }}>
      {props.children}
    </HomeStoreContext.Provider>
  )
}

export default withAuthStore(HomeStoreProvider)
